import React, { useState, useEffect, useCallback } from "react";
import {
  Box,
  Typography,
  Chip,
  CircularProgress,
  Collapse,
  IconButton,
  Card,
  CardContent,
  Stack,
  Grid,
  Button,
  Switch,
  FormControlLabel,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from "@mui/material";
import {
  Tune,
  ExpandMore as ExpandMoreIcon,
  CheckBox as CheckBoxIcon,
  HorizontalRule as SeparatorIcon,
  WrapText as WrapIcon,
  FormatAlignLeft as AlignIcon,
  TextFields as PrefixIcon,
  ViewList as LayoutIcon,
  CloudUpload as UploadIcon,
} from "@mui/icons-material";

import { DEFAULT_CONFIG } from "./utils/DefaultConfigWitness";
import { useGetDocumentWitnessConfig } from "../../../../hooks/useGetDocumentWitnessConfig";
import { useSaveDocumentWitnessConfig } from "../../../../hooks/useSaveDocumentWitnessConfig";
import { FieldsSelector } from "./components/FieldsSelector";
import { SeparatorSelector } from "./components/SeparatorSelector";
import { WrapperSelector } from "./components/WrapperSelector";
import { AlignmentSelector } from "./components/AlignmentSelector";
import { PrefixInput } from "./components/PrefixInput";
import { SuffixInput } from "./components/SuffixInput";
import { LayoutSelector } from "./components/LayoutSelector";
import { ConfigName } from "./components/configName";

const computeDiff = (original, current) => {
  if (!original) return current;
  return Object.entries(current).reduce((acc, [key, value]) => {
    const originalValue = original[key];
    const isDifferent = Array.isArray(value)
      ? JSON.stringify(value) !== JSON.stringify(originalValue)
      : value !== originalValue;
    if (isDifferent) acc[key] = value;
    return acc;
  }, {});
};

const createNewConfig = () => ({
  ...DEFAULT_CONFIG,
  id_config: null,
  name_config: "",
  selected_fields: [],
  separator: "",
  wrapper_start: "",
  wrapper_end: "",
  alignment: "",
  prefix: "",
  suffix: "",
  layout: "",
  list_type: "",
  flg_active: true,
});

const isConfigActive = (config) =>
  config?.flg_active === 1 ||
  config?.flg_active === true ||
  String(config?.flg_active).toLowerCase() === "true";

const normalizeConfigs = (fetched) => {
  if (Array.isArray(fetched)) return fetched;
  return fetched && typeof fetched === "object" ? [fetched] : [];
};

const SectionAccordion = ({
  id,
  icon: Icon,
  title,
  isOpen,
  onToggle,
  children,
  disabled = false,
}) => (
  <Box
    sx={{
      borderRadius: "8px",
      border: isOpen ? "1px solid #90caf9" : "1px solid #e8eef7",
      overflow: "hidden",
      transition: "border-color 0.2s ease",
      backgroundColor: "#fff",
      opacity: disabled ? 0.55 : 1,
    }}
  >
    <Box
      id={`section-header-${id}`}
      onClick={disabled ? undefined : onToggle}
      aria-disabled={disabled}
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 1,
        px: 1.5,
        py: 1,
        cursor: disabled ? "not-allowed" : "pointer",
        userSelect: "none",
        backgroundColor: isOpen ? "#e8f0fe" : "#f5f8ff",
        transition: "background-color 0.2s ease",
        "&:hover": {
          backgroundColor: isOpen ? "#dce8fd" : "#eaf0fb",
        },
      }}
    >
      {Icon && (
        <Icon
          sx={{
            fontSize: 17,
            color: isOpen ? "#1976d2" : "#6b7fa6",
            transition: "color 0.2s ease",
            flexShrink: 0,
          }}
        />
      )}
      <Typography
        variant="caption"
        fontWeight={isOpen ? 700 : 600}
        sx={{
          color: isOpen ? "#1565c0" : "#455a7a",
          transition: "color 0.2s ease",
          flexGrow: 1,
          fontSize: "0.78rem",
          letterSpacing: "0.01em",
        }}
      >
        {title}
      </Typography>
      <IconButton
        size="small"
        disabled={disabled}
        disableRipple
        sx={{
          p: 0,
          transform: isOpen ? "rotate(180deg)" : "rotate(0deg)",
          transition: "transform 0.25s ease",
          color: isOpen ? "#1976d2" : "#90a4c4",
        }}
      >
        <ExpandMoreIcon sx={{ fontSize: 18 }} />
      </IconButton>
    </Box>

    <Collapse in={isOpen} timeout={220} unmountOnExit>
      <fieldset
        disabled={disabled}
        style={{ border: 0, margin: 0, padding: 0, minWidth: 0 }}
      >
        <Box sx={{ px: 2, py: 1.5 }}>{children}</Box>
      </fieldset>
    </Collapse>
  </Box>
);

const SECTION_IDS = {
  NAME_CONFIG: "name_config",
  FIELDS: "fields",
  SEPARATOR: "separator",
  WRAPPER: "wrapper",
  ALIGNMENT: "alignment",
  PREFIX_SUFFIX: "prefix_suffix",
  LAYOUT: "layout",
};

export const WitnessConfigPanel = ({
  token,
  documentId,
  onConfigChange,
  alignmentNavigationRequest = 0,
}) => {
  const { loading, fetchConfig } = useGetDocumentWitnessConfig(token);
  const { saveConfig, saving } = useSaveDocumentWitnessConfig(token);

  const [activeSection, setActiveSection] = useState(SECTION_IDS.NAME_CONFIG);

  const [form, setForm] = useState(createNewConfig);
  const [originalConfig, setOriginalConfig] = useState(null);
  const [configsList, setConfigsList] = useState([]);
  const isSavedConfig = Boolean(form.id_config);
  const canEditSettings = isSavedConfig || Boolean(form.name_config?.trim());

  useEffect(() => {
    if (!documentId) return;

    fetchConfig(documentId).then((fetched) => {
      const allConfigs = normalizeConfigs(fetched);
      const activeConfig = allConfigs.find(isConfigActive);

      setConfigsList(allConfigs);

      if (activeConfig) {
        const merged = { ...DEFAULT_CONFIG, ...activeConfig };
        setForm(merged);
        setOriginalConfig(merged);
      } else {
        setForm(createNewConfig());
        setOriginalConfig(null);
      }
    });
  }, [documentId, fetchConfig]);

  useEffect(() => {
    if (!alignmentNavigationRequest) return;

    setActiveSection(SECTION_IDS.ALIGNMENT);
    const frameId = window.requestAnimationFrame(() => {
      const alignmentHeader = document.getElementById(
        `section-header-${SECTION_IDS.ALIGNMENT}`,
      );
      const target =
        alignmentHeader || document.getElementById("witness-config-panel");
      target?.scrollIntoView({
        behavior: "smooth",
        block: alignmentHeader ? "center" : "start",
      });
    });

    return () => window.cancelAnimationFrame(frameId);
  }, [alignmentNavigationRequest]);

  const setField = useCallback(
    (key, value) => {
      setForm((prev) => {
        const next = { ...prev, [key]: value };
        onConfigChange?.(next);
        return next;
      });
    },
    [onConfigChange],
  );

  const handleWrapperChange = useCallback(
    (start, end) => {
      setForm((prev) => {
        const next = { ...prev, wrapper_start: start, wrapper_end: end };
        onConfigChange?.(next);
        return next;
      });
    },
    [onConfigChange],
  );

  const handleSave = async () => {
    const isCreating = !form.id_config;
    const payload = isCreating
      ? { ...form, flg_active: true }
      : computeDiff(originalConfig, form);

    const success = await saveConfig(documentId, payload, form.id_config);
    if (!success) return;

    if (isCreating) {
      const refreshedConfigs = normalizeConfigs(await fetchConfig(documentId));
      setConfigsList(refreshedConfigs);

      const savedConfig =
        refreshedConfigs.find(
          (config) =>
            isConfigActive(config) && config.name_config === form.name_config,
        ) ?? refreshedConfigs.find(isConfigActive);

      if (savedConfig) {
        const merged = { ...DEFAULT_CONFIG, ...savedConfig };
        setForm(merged);
        setOriginalConfig(merged);
      } else {
        const savedForm = { ...form, flg_active: true };
        setForm(savedForm);
        setOriginalConfig(savedForm);
      }
      return;
    }

    const refreshed = await fetchConfig(documentId);
    const refreshedConfigs = normalizeConfigs(refreshed);
    const savedConfig = refreshedConfigs.find(
      (config) => String(config.id_config) === String(form.id_config),
    );

    if (refreshedConfigs.length > 0) {
      setConfigsList(refreshedConfigs);
    } else {
      setConfigsList((prev) =>
        prev.map((config) =>
          String(config.id_config) === String(form.id_config)
            ? { ...config, ...form }
            : config,
        ),
      );
    }

    const savedForm = savedConfig
      ? { ...DEFAULT_CONFIG, ...savedConfig }
      : { ...form };
    setForm(savedForm);
    setOriginalConfig(savedForm);
  };

  const toggleSection = (id) => {
    setActiveSection((prev) => (prev === id ? null : id));
  };

  const hasDiff = Object.keys(computeDiff(originalConfig, form)).length > 0;

  return (
    <Card
      id="witness-config-panel"
      sx={{
        borderRadius: "16px",
        bgcolor: "#ffffff",
        width: "100%",
        minWidth: 0,
      }}
    >
      <CardContent sx={{ p: 4 }}>
        <Box display="flex" alignItems="center" gap={2} mb={3}>
          <Tune sx={{ fontSize: 20, color: "#1976d2" }} />
          <Typography variant="h6" fontWeight="bold">
            Configuración del formato de los Testigos
          </Typography>
          {hasDiff && (
            <Chip
              label="Sin guardar"
              size="small"
              color="warning"
              variant="outlined"
              sx={{ fontSize: "0.65rem", height: 18 }}
            />
          )}
          {loading && <CircularProgress size={14} />}
        </Box>

        <Typography variant="body2" color="text.secondary" mb={3}>
          Configura los campos, separador, envoltorio, alineación, prefijos,
          sufijos y diseño de los testigos.
        </Typography>

        {loading ? (
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, py: 2 }}>
            <CircularProgress size={16} />
            <Typography variant="body2" color="text.secondary">
              Cargando configuración…
            </Typography>
          </Box>
        ) : (
          <>
            <FormControl fullWidth size="small" sx={{ mb: 3 }}>
              <InputLabel id="select-config-label">
                Configuración guardada
              </InputLabel>
              <Select
                labelId="select-config-label"
                value={form.id_config || ""}
                label="Configuración guardada"
                onChange={(e) => {
                  const selectedId = e.target.value;
                  const selectedConfig = configsList.find(
                    (config) => String(config.id_config) === String(selectedId),
                  );

                  if (selectedConfig) {
                    const merged = { ...DEFAULT_CONFIG, ...selectedConfig };
                    setForm(merged);
                    setOriginalConfig(merged);
                    setActiveSection(SECTION_IDS.FIELDS);
                  } else {
                    setForm(createNewConfig());
                    setOriginalConfig(null);
                    setActiveSection(SECTION_IDS.NAME_CONFIG);
                  }
                }}
              >
                <MenuItem value="">
                  <em>Nueva configuración</em>
                </MenuItem>
                {configsList.map((c) => (
                  <MenuItem key={c.id_config} value={c.id_config}>
                    {c.name_config || `Configuración ${c.id_config}`}{" "}
                    {isConfigActive(c) ? "(Activa)" : ""}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControlLabel
              control={
                <Switch
                  checked={form.flg_active === true || form.flg_active === 1}
                  disabled={!isSavedConfig || !canEditSettings}
                  onChange={(e) =>
                    setField("flg_active", e.target.checked ? 1 : 0)
                  }
                  sx={{
                    "& .MuiSwitch-switchBase.Mui-checked": {
                      color: "#1976d2",
                    },
                    "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": {
                      bgcolor: "#1976d2",
                    },
                  }}
                />
              }
              label={
                form.flg_active === true || form.flg_active === 1
                  ? "Desactivar configuración"
                  : "Activar configuración"
              }
              sx={{ mb: 3 }}
            />

            {(isSavedConfig ||
              form.flg_active === true ||
              form.flg_active === 1) && (
              <Stack spacing={2}>
                <SectionAccordion
                  id={SECTION_IDS.NAME_CONFIG}
                  icon={PrefixIcon}
                  title="Nombre de la configuración"
                  isOpen={activeSection === SECTION_IDS.NAME_CONFIG}
                  onToggle={() => toggleSection(SECTION_IDS.NAME_CONFIG)}
                >
                  <ConfigName
                    value={form.name_config}
                    onChange={(v) => setField("name_config", v)}
                    disabled={isSavedConfig}
                    helperText={
                      !isSavedConfig && !form.name_config?.trim()
                        ? "Escribe un nombre para habilitar las demás opciones."
                        : undefined
                    }
                  />
                </SectionAccordion>

                <SectionAccordion
                  id={SECTION_IDS.FIELDS}
                  icon={CheckBoxIcon}
                  title="Campos del testigo"
                  isOpen={activeSection === SECTION_IDS.FIELDS}
                  onToggle={() => toggleSection(SECTION_IDS.FIELDS)}
                  disabled={!canEditSettings}
                >
                  <FieldsSelector
                    value={form.selected_fields}
                    onChange={(v) => setField("selected_fields", v)}
                  />
                </SectionAccordion>

                <SectionAccordion
                  id={SECTION_IDS.SEPARATOR}
                  icon={SeparatorIcon}
                  title="Separador"
                  isOpen={activeSection === SECTION_IDS.SEPARATOR}
                  onToggle={() => toggleSection(SECTION_IDS.SEPARATOR)}
                  disabled={!canEditSettings}
                >
                  <SeparatorSelector
                    value={form.separator}
                    onChange={(v) => setField("separator", v)}
                  />
                </SectionAccordion>

                <SectionAccordion
                  id={SECTION_IDS.WRAPPER}
                  icon={WrapIcon}
                  title="Envoltorio (wrapper)"
                  isOpen={activeSection === SECTION_IDS.WRAPPER}
                  onToggle={() => toggleSection(SECTION_IDS.WRAPPER)}
                  disabled={!canEditSettings}
                >
                  <WrapperSelector
                    wrapperStart={form.wrapper_start}
                    onWrapperChange={handleWrapperChange}
                  />
                </SectionAccordion>

                <SectionAccordion
                  id={SECTION_IDS.ALIGNMENT}
                  icon={AlignIcon}
                  title="Alineación"
                  isOpen={activeSection === SECTION_IDS.ALIGNMENT}
                  onToggle={() => toggleSection(SECTION_IDS.ALIGNMENT)}
                  disabled={!canEditSettings}
                >
                  <AlignmentSelector
                    value={form.alignment}
                    onChange={(v) => setField("alignment", v)}
                  />
                </SectionAccordion>

                <SectionAccordion
                  id={SECTION_IDS.PREFIX_SUFFIX}
                  icon={PrefixIcon}
                  title="Prefijo y Sufijo"
                  isOpen={activeSection === SECTION_IDS.PREFIX_SUFFIX}
                  onToggle={() => toggleSection(SECTION_IDS.PREFIX_SUFFIX)}
                  disabled={!canEditSettings}
                >
                  <Box
                    sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}
                  >
                    <PrefixInput
                      value={form.prefix}
                      onChange={(v) => setField("prefix", v)}
                    />
                    <SuffixInput
                      value={form.suffix}
                      onChange={(v) => setField("suffix", v)}
                    />
                  </Box>
                </SectionAccordion>

                <SectionAccordion
                  id={SECTION_IDS.LAYOUT}
                  icon={LayoutIcon}
                  title="Diseño (Layout)"
                  isOpen={activeSection === SECTION_IDS.LAYOUT}
                  onToggle={() => toggleSection(SECTION_IDS.LAYOUT)}
                  disabled={!canEditSettings}
                >
                  <LayoutSelector
                    layout={form.layout}
                    listType={form.list_type}
                    onLayoutChange={(v) => {
                      setField("layout", v);
                      if (v === "CONCATENATED") setField("list_type", "NONE");
                      if (v === "LIST" && form.list_type === "NONE")
                        setField("list_type", "BULLET");
                    }}
                    onListTypeChange={(v) => setField("list_type", v)}
                    wrapperStart={form.wrapper_start}
                    wrapperEnd={form.wrapper_end}
                    separator={form.separator}
                  />
                </SectionAccordion>
              </Stack>
            )}
          </>
        )}

        {!loading && (
          <Grid container justifyContent="flex-end">
            <Button
              id="witness-config-save-btn"
              variant="contained"
              onClick={handleSave}
              disabled={!hasDiff || saving || !canEditSettings}
              startIcon={<UploadIcon sx={{ fontSize: 20 }} />}
              sx={{
                mt: 3,
                bgcolor: "#1976d2",
                color: "white",
                borderRadius: "50px",
                px: 4,
                py: 1.5,
                textTransform: "capitalize",
                "&:hover": { bgcolor: "#1565c0" },
                "&:disabled": {
                  bgcolor: "#e0e0e0",
                  color: "#9e9e9e",
                },
              }}
            >
              {saving ? "Cargando…" : "Guardar Cambios"}
            </Button>
          </Grid>
        )}
      </CardContent>
    </Card>
  );
};
